import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress, Alert } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { tenXService } from '../../../../../services/TenXService';
import { type LogEvent } from '../logTypes';
import styles from './liveView.module.css';

export const POLL_INTERVAL_MS = 5000;
export const AUTO_PAUSE_MS = 15 * 60 * 1000;

interface LiveViewProps {
    deviceName: string;
    onBack: () => void;
}

const LiveView: React.FC<LiveViewProps> = ({ deviceName, onBack }) => {
    const [logs, setLogs] = useState<LogEvent[]>([]);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pausedByTimeout, setPausedByTimeout] = useState<boolean>(false);

    // Ref so the polling interval always reads the latest token without stale closures
    const nextTokenRef = useRef<string | null>(null);
    nextTokenRef.current = nextToken;

    const logDisplayRef = useRef<HTMLDivElement>(null);
    const autoScrollRef = useRef<boolean>(true);

    // Scroll to bottom when new logs arrive, if auto-scroll is active
    useEffect(() => {
        if (autoScrollRef.current && logDisplayRef.current) {
            logDisplayRef.current.scrollTop = logDisplayRef.current.scrollHeight;
        }
    }, [logs]);

    useEffect(() => {
        if (!isPlaying) return;

        const intervalId = setInterval(async () => {
            if (!nextTokenRef.current) return;
            try {
                const result = await tenXService.getNextDeviceLogs(deviceName, nextTokenRef.current);
                if (result.logEvents.length > 0) {
                    setLogs(prev => [...prev, ...result.logEvents]);
                    setNextToken(result.nextToken);
                }
            } catch (err) {
                setIsPlaying(false);
                setError(err instanceof Error ? err.message : 'Failed to fetch logs');
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [isPlaying, deviceName]);

    useEffect(() => {
        if (!isPlaying) return;

        const timerId = setTimeout(() => {
            setIsPlaying(false);
            setPausedByTimeout(true);
        }, AUTO_PAUSE_MS);

        return () => clearTimeout(timerId);
    }, [isPlaying]);

    const handlePlay = async () => {
        if (isLoading) return;
        setError(null);
        setPausedByTimeout(false);

        if (nextToken === null) {
            setIsLoading(true);
            try {
                const result = await tenXService.getDeviceLogs(deviceName);
                setLogs(result.logEvents);
                setNextToken(result.nextToken);
                setIsPlaying(true);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch logs');
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsPlaying(true);
        }
    };

    const handlePause = () => setIsPlaying(false);

    const handleScroll = () => {
        if (!logDisplayRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = logDisplayRef.current;
        autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
    };

    return (
        <Box>
            <Box className={styles.controls}>
                <Typography variant="subtitle1" fontWeight="medium">Live View</Typography>
                <Box className={styles.controlButtons}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={
                            isLoading
                                ? <CircularProgress size={14} color="inherit" />
                                : isPlaying ? <PauseIcon /> : <PlayArrowIcon />
                        }
                        onClick={isPlaying ? handlePause : handlePlay}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Loading...' : isPlaying ? 'Pause' : 'Play'}
                    </Button>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                    >
                        Back
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {pausedByTimeout && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    Live view paused after 15 minutes. Press Play to resume.
                </Alert>
            )}

            {logs.length > 0 && (
                <Typography variant="caption" className={styles.logSummary}>
                    {logs[0].timestamp} → {logs[logs.length - 1].timestamp} · {logs.length.toLocaleString()} events
                </Typography>
            )}

            <Box
                ref={logDisplayRef}
                onScroll={handleScroll}
                className={styles.logDisplay}
            >
                {!isLoading && nextToken === null && (
                    <span className={styles.emptyMessage}>
                        Press play to start viewing logs from the past 10 minutes
                    </span>
                )}
                {!isLoading && nextToken !== null && logs.length === 0 && (
                    <span className={styles.emptyMessage}>
                        No logs found for this device in the past 10 minutes
                    </span>
                )}
                {logs.map((log, i) => (
                    <div key={i} className={styles.logLine}>
                        <span className={styles.timestamp}>{log.timestamp}</span>
                        {' '}{log.message}
                    </div>
                ))}
            </Box>
        </Box>
    );
};

export default LiveView;
